"use client";

import { X } from "lucide-react";
import * as React from "react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

export interface PropertyPickerOption<T = string> {
  value: T;
  label: string;
  icon?: React.ReactNode;
  displayLabel?: React.ReactNode;
  /**
   * Exclude this option from the dropdown list but keep it available for
   * resolving an existing value's label/icon (e.g. legacy or system-set
   * statuses that aren't valid user-pickable targets).
   */
  hidden?: boolean;
}

export interface PropertyPickerProps<T> {
  value: T | null;
  onChange: (value: T) => void;
  disabled?: boolean;
  placeholder?: string;
  /** Prefix label shown before the value (e.g. "Type value") */
  label?: string;
  className?: string;
  icon?: React.ReactNode;
  iconOnly?: boolean;
  displayValue?: React.ReactNode;
  /** When true, the trigger renders displayValue directly without button styling */
  bare?: boolean;
  showClear?: boolean;
  onClear?: () => void;
  children: React.ReactNode;
}

export function PropertyPicker<T>({
  value,
  onChange,
  disabled,
  placeholder = "Select...",
  label,
  className,
  icon,
  iconOnly,
  displayValue,
  bare,
  showClear,
  onClear,
  children,
}: PropertyPickerProps<T>) {
  const [open, setOpen] = useState(false);
  const hasValue = value != null && value !== "";
  const useBare = bare && hasValue && displayValue != null;

  const popoverContent = (
    <PopoverContent className="w-auto min-w-36 p-0" align="start">
      {React.Children.map(children, (child) => {
        if (React.isValidElement(child)) {
          return React.cloneElement(child as React.ReactElement<any>, {
            onSelect: (optionValue: T) => {
              onChange(optionValue);
              setOpen(false);
            },
            selectedValue: value,
          });
        }
        return child;
      })}
    </PopoverContent>
  );

  const trigger = (
    <Popover open={open} onOpenChange={setOpen}>
      {useBare ? (
        <PopoverTrigger
          disabled={disabled}
          nativeButton={false}
          className={cn("cursor-pointer hover:opacity-80 transition-opacity", className)}
        >
          {displayValue}
        </PopoverTrigger>
      ) : (
        <PopoverTrigger
          disabled={disabled}
          render={
            <Button
              variant="ghost"
              size="sm"
              className={cn(
                "h-7 w-fit gap-1.5 rounded-full px-2 text-xs font-normal hover:text-foreground",
                hasValue ? "text-foreground" : "text-muted-foreground",
                className,
              )}
            >
              {icon}
              {!iconOnly &&
                (hasValue ? (
                  <span className="truncate">
                    {label && <span className="text-muted-foreground">{label} </span>}
                    {displayValue != null ? displayValue : String(value)}
                  </span>
                ) : (
                  <span>{placeholder}</span>
                ))}
            </Button>
          }
        />
      )}
      {popoverContent}
    </Popover>
  );

  if (showClear && onClear && value != null) {
    return (
      <div className="flex items-center gap-1">
        {trigger}
        <Button variant="ghost" size="icon-xs" onClick={onClear} className="shrink-0">
          <X className="size-3" />
        </Button>
      </div>
    );
  }

  return trigger;
}
